import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../redux-toolkit/store/store";
import { formatDate } from "../../helpers/date";
import { getAuthToken } from "../../helpers/userDetails";
import { socket } from "../../socketService";
import { pushNewMessage } from "../../redux-toolkit/slices/chatSlice";

// Typing Indicator Component
const TypingIndicator = () => (
  <div className="message-container other">
    <div className="message-avatar">
      <span>Dr</span>
    </div>
    <div className="message-content">
      <div className="typing-indicator">
        <div className="typing-dot"></div>
        <div className="typing-dot"></div>
        <div className="typing-dot"></div>
      </div>
    </div>
  </div>
);

// Skeleton Loader Component
const MessageSkeleton = ({ isUser = false }) => (
  <div className={`message-container ${isUser ? "me" : "other"}`}>
    {!isUser && (
      <div className="message-avatar">
        <span>Dr</span>
      </div>
    )}
    <div className="message-content">
      <div className={`message ${isUser ? "me" : "other"}`}>
        <div className="skeleton-text">
          {isUser ? (
            <>
              <div className="skeleton-line skeleton-line-short"></div>
              <div className="skeleton-line skeleton-line-medium"></div>
            </>
          ) : (
            <>
              <div className="skeleton-line skeleton-line-medium"></div>
              <div className="skeleton-line skeleton-line-long"></div>
              <div className="skeleton-line skeleton-line-short"></div>
            </>
          )}
        </div>
      </div>
      <span className="message-time">
        <div className="skeleton-time"></div>
      </span>
    </div>
    {isUser && (
      <div className="message-avatar">
        <span>U</span>
      </div>
    )}
  </div>
);

// Chat Skeleton Loader
const ChatSkeleton = () => (
  <>
    <MessageSkeleton isUser={false} />
    <MessageSkeleton isUser={true} />
    <MessageSkeleton isUser={false} />
    <MessageSkeleton isUser={true} />
    <MessageSkeleton isUser={false} />
  </>
);

const ChatWindow = () => {
  const { userChat, isAbleToChat } = useSelector(
    (state: RootState) => state.chat
  );
  const { userProfile } = useSelector((state: RootState) => state.auth);
  const [message, setMessage] = useState<string>("");
  const [isTyping, setIsTyping] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [userChat, isTyping]);

  const sendMessage = () => {
    if (!message.trim()) return;
    
    socket.emit("sendMessage", {
      token: getAuthToken(),
      message: message,
    });
    dispatch(
      pushNewMessage({
        _id: Math.random().toString(),
        content: message,
        createdAt: new Date(),
        receiverId: "",
        senderId: userProfile?.id || "",
        isRead: false,
        updatedAt: new Date(),
      })
    );
    setMessage("");
  };

  // Show skeleton loader when chat is empty
  const showSkeleton = userChat.length === 0;
  return (
    <div className="chat-window">
      <div className="chat-header">
        <div className="chat-header-info">
          <div className="chat-header-avatar">
            <span>Dr</span>
          </div>
          <div className="chat-header-details">
            <h3>Dr. Sarah Johnson</h3>
            <p>Online now</p>
          </div>
        </div>
        <div className="chat-header-status">
          <div className="status-indicator"></div>
          <span className="status-text">Active</span>
        </div>
      </div>
      {isAbleToChat === true ? (
        <>
          <div className="chat-messages">
            {showSkeleton ? (
              <ChatSkeleton />
            ) : (
              userChat.map((msg, index) => (
                <div
                  key={index}
                  className={`message-container ${
                    msg.senderId !== userProfile?._id ? "other" : "me"
                  }`}
                >
                  {msg.senderId !== userProfile?._id && (
                    <div className="message-avatar">
                      <span>Dr</span>
                    </div>
                  )}
                  <div className="message-content">
                    <div
                      className={`message ${
                        msg.senderId !== userProfile?._id ? "other" : "me"
                      }`}
                    >
                      <p>{msg.content}</p>
                    </div>
                    <span className="message-time">
                      {formatDate(msg.createdAt)}
                    </span>
                  </div>
                  {msg.senderId === userProfile?._id && (
                    <div className="message-avatar">
                      <span>{userProfile?.firstName?.charAt(0) || "U"}</span>
                    </div>
                  )}
                </div>
              ))
            )}
            {isTyping && <TypingIndicator />}
            <div ref={messagesEndRef} />
          </div>
          <div className="chat-input">
            <form className="chat-input-form" onSubmit={(e) => { e.preventDefault(); sendMessage(); }}>
              <textarea
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                placeholder="Type your message here..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={1}
              />
              <button type="submit">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13"></line>
                  <polygon points="22,2 15,22 11,13 2,9 22,2"></polygon>
                </svg>
              </button>
            </form>
            <div className="chat-input-actions">
              <div className="chat-input-attachments">
                <button type="button" className="attachment-button" title="Attach file">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66L9.64 16.2a2 2 0 0 1-2.83-2.83l8.49-8.49"></path>
                  </svg>
                </button>
                <button type="button" className="attachment-button" title="Attach image">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect width="18" height="18" x="3" y="3" rx="2" ry="2"></rect>
                    <circle cx="9" cy="9" r="2"></circle>
                    <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"></path>
                  </svg>
                </button>
              </div>
              <span className="chat-input-hint">Press Enter to send</span>
            </div>
          </div>
        </>
      ) : (
        <div className="chat-restricted">
          <h1>Please Order Cream Before Start Chatting</h1>
        </div>
      )}
    </div>
  );
};

export default ChatWindow;
